import {
  AlertCircle,
  CheckCircle2,
  Flame,
  Minus,
  TrendingUp,
  Zap
} from 'lucide-react';
import { useRef } from 'react';

import Button from '../Common/Button';
import CommonDialog from '../Common/Dialog';

type RecommendedKeyword = {
  keyword: string;
  importance: string;
  reason: string;
  estimatedScoreBoost: number;
};

type SectionScores = {
  skills: number;
  experience: number;
  projects: number;
  education: number;
  certifications: number;
};

export type BoostATSResultData = {
  atsScore: number;
  atsLevel: string;
  potentialAtsScoreAfterOptimization: number;
  estimatedScoreIncrease: number;
  keywordMatchPercentage: number;
  matchedKeywords: string[];
  criticalMissingKeywords: string[];
  recommendedKeywords: RecommendedKeyword[];
  sectionScores: SectionScores;
  strengths: string[];
  weaknesses: string[];
  improvementSuggestions: string[];
  overallAnalysis: string;
};

const ScoreRing = ({
  score,
  label,
  size = 130,
  color,
  glowColor
}: {
  score: number;
  label: string;
  size?: number;
  color: string;
  glowColor: string;
}) => {
  const r = (size - 16) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (score / 100) * circumference;
  const gradId = `grad-${label.replace(/\s/g, '')}`;

  return (
    <div className='flex flex-col items-center'>
      <div className='relative' style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          style={{ transform: 'rotate(-90deg)' }}
          className='relative z-[1]'
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill='none'
            stroke='#d1d5db'
            strokeWidth='10'
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill='none'
            stroke={`url(#${gradId})`}
            strokeWidth='10'
            strokeLinecap='round'
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{
              filter: `drop-shadow(0 0 4px ${glowColor}50)`
            }}
          />
          <defs>
            <linearGradient id={gradId} x1='0%' y1='0%' x2='100%' y2='100%'>
              <stop offset='0%' stopColor={color} />
              <stop offset='100%' stopColor={glowColor} />
            </linearGradient>
          </defs>
        </svg>
        <div className='absolute inset-0 z-[2] flex flex-col items-center justify-center'>
          <span
            className='font-extrabold text-gray-900'
            style={{
              fontSize: size * 0.3,
              lineHeight: 1,
              letterSpacing: '-0.02em'
            }}
          >
            {score}
          </span>
          <span className='mt-0.5 text-[11px] font-semibold text-gray-500'>
            /100
          </span>
        </div>
      </div>
      <p className='mt-2.5 text-[13px] font-bold text-gray-700'>{label}</p>
    </div>
  );
};

const SectionBar = ({ label, score }: { label: string; score: number }) => (
  <div className='flex items-center gap-3 py-1.5'>
    <span className='w-[100px] shrink-0 text-xs text-gray-500'>{label}</span>
    <div className='h-2 flex-1 overflow-hidden rounded-full bg-gray-100'>
      <div
        className='h-full rounded-full bg-brand'
        style={{ width: `${Math.max(score, 3)}%` }}
      />
    </div>
    <span className='w-8 text-right text-[13px] font-extrabold text-brand'>
      {score}
    </span>
  </div>
);

const LevelBadge = ({ level }: { level: string }) => {
  const lower = (level ?? '').toLowerCase();
  let classes = 'bg-red-50 text-red-600 border-red-200';
  let dotClass = 'bg-red-500';

  if (lower.includes('good') || lower.includes('excellent')) {
    classes = 'bg-green-50 text-green-700 border-green-200';
    dotClass = 'bg-green-500';
  } else if (lower.includes('moderate') || lower.includes('average')) {
    classes = 'bg-amber-50 text-amber-700 border-amber-200';
    dotClass = 'bg-amber-500';
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border-[1.5px] px-3.5 py-1.5 text-xs font-bold ${classes}`}
    >
      <span className={`h-[7px] w-[7px] rounded-full ${dotClass}`} />
      {level}
    </span>
  );
};

const RecommendedCard = ({
  keyword,
  importance,
  reason
}: RecommendedKeyword) => {
  const lower = (importance ?? '').toLowerCase();
  let badgeClasses = 'bg-red-50 text-red-600';
  let Icon = Flame;
  if (lower === 'medium') {
    badgeClasses = 'bg-amber-50 text-amber-600';
    Icon = Zap;
  } else if (lower === 'low') {
    badgeClasses = 'bg-green-50 text-green-600';
    Icon = Minus;
  }

  return (
    <div className='flex items-start gap-3.5 rounded-xl border border-gray-200 bg-white px-3.5 py-3'>
      <div className='min-w-0 flex-1'>
        <div className='text-[13px] font-bold text-gray-900'>{keyword}</div>
        <p className='mt-0.5 text-[11px] leading-snug text-gray-500'>
          {reason}
        </p>
      </div>
      <span
        className={`flex shrink-0 items-center gap-1.5 self-center rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase ${badgeClasses}`}
      >
        <Icon size={12} className='shrink-0' />
        <span className='relative top-[1px]'>{importance}</span>
      </span>
    </div>
  );
};

const BoostATSResults = ({
  isOpen,
  close,
  data,
  onTryAnother
}: {
  isOpen: boolean;
  close: () => void;
  data: BoostATSResultData;
  onTryAnother: () => void;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  return (
    <CommonDialog
      isOpen={isOpen}
      close={close}
      dialogTitle='ATS Analysis Report'
      width='w-full max-w-[660px]'
      childClasses='p-0'
    >
      <div ref={scrollRef} className='max-h-[75vh] overflow-y-auto'>
        <div
          className='relative overflow-hidden border-b border-gray-200 px-6 pb-6 pt-7'
          style={{
            background:
              'linear-gradient(160deg, #f0f4ff 0%, #e8edff 30%, #dbeafe 60%, #eff6ff 100%)'
          }}
        >
          <div className='relative z-[1] flex items-center justify-center'>
            <ScoreRing
              score={data.atsScore}
              label='Current Score'
              color='#f97316'
              glowColor='#fb923c'
            />

            <div className='flex min-w-[130px] flex-col items-center px-3'>
              <div className='inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-green-300 bg-green-50 px-4 py-[7px] text-[15px] font-extrabold text-emerald-600'>
                <TrendingUp size={15} />+{data.estimatedScoreIncrease}
              </div>
              <p className='mt-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500'>
                Potential Gain
              </p>
              <div className='mt-2 flex w-full items-center'>
                <div className='h-0.5 flex-1 bg-emerald-600' />
                <div
                  className='shrink-0'
                  style={{
                    width: 0,
                    height: 0,
                    borderTop: '5px solid transparent',
                    borderBottom: '5px solid transparent',
                    borderLeft: '8px solid #059669'
                  }}
                />
              </div>
            </div>

            <ScoreRing
              score={data.potentialAtsScoreAfterOptimization}
              label='Potential Score'
              color='#2563eb'
              glowColor='#3b82f6'
            />
          </div>

          <div className='relative z-[1] mt-5 flex items-center justify-center gap-3.5'>
            <span className='text-sm font-semibold text-gray-700'>
              Keyword match{' '}
              <span className='text-base font-extrabold text-gray-900'>
                {data.keywordMatchPercentage}%
              </span>
            </span>
            <span className='h-5 w-px bg-gray-300' />
            <LevelBadge level={data.atsLevel} />
          </div>
        </div>

        <div className='space-y-7 p-6'>
          {data.sectionScores && (
            <div>
              <h4 className='mb-3.5 flex items-center gap-2 text-sm font-extrabold text-gray-900'>
                <span className='h-4 w-1 rounded-sm bg-brand' />
                Resume Section Scores
              </h4>
              <div className='grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-gray-200 bg-white p-4 px-5'>
                <SectionBar
                  label='Skills'
                  score={data.sectionScores?.skills ?? 0}
                />
                <SectionBar
                  label='Experience'
                  score={data.sectionScores?.experience ?? 0}
                />
                <SectionBar
                  label='Projects'
                  score={data.sectionScores?.projects ?? 0}
                />
                <SectionBar
                  label='Education'
                  score={data.sectionScores?.education ?? 0}
                />
                <SectionBar
                  label='Certifications'
                  score={data.sectionScores?.certifications ?? 0}
                />
              </div>
            </div>
          )}

          {((data.matchedKeywords?.length ?? 0) > 0 ||
            (data.criticalMissingKeywords?.length ?? 0) > 0) && (
            <div>
              <h4 className='mb-3.5 flex items-center gap-2 text-sm font-extrabold text-gray-900'>
                <span className='h-4 w-1 rounded-sm bg-brand' />
                Keyword Analysis
              </h4>
              <div className='grid grid-cols-2 gap-3'>
                {(data.matchedKeywords?.length ?? 0) > 0 && (
                  <div className='rounded-xl border border-green-200 bg-[#f9fefb] p-4'>
                    <p className='mb-3 flex items-center gap-1.5 text-xs font-bold text-green-700'>
                      <CheckCircle2 size={14} />
                      Matched
                      <span className='ml-1 rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-extrabold'>
                        {data.matchedKeywords?.length ?? 0}
                      </span>
                    </p>
                    <div className='flex flex-wrap gap-1.5'>
                      {data.matchedKeywords?.map(kw => (
                        <span
                          key={kw}
                          className='rounded-md bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700'
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(data.criticalMissingKeywords?.length ?? 0) > 0 && (
                  <div className='rounded-xl border border-red-200 bg-[#fffbfb] p-4'>
                    <p className='mb-3 flex items-center gap-1.5 text-xs font-bold text-red-600'>
                      <AlertCircle size={14} />
                      Missing
                      <span className='ml-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-extrabold'>
                        {data.criticalMissingKeywords?.length ?? 0}
                      </span>
                    </p>
                    <div className='flex flex-wrap gap-1.5'>
                      {data.criticalMissingKeywords?.map(kw => (
                        <span
                          key={kw}
                          className='rounded-md bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600'
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {(data.recommendedKeywords?.length ?? 0) > 0 && (
            <div>
              <h4 className='mb-3.5 flex items-center gap-2 text-sm font-extrabold text-gray-900'>
                <span className='h-4 w-1 rounded-sm bg-brand' />
                Recommended Keywords
              </h4>
              <div className='space-y-2'>
                {data.recommendedKeywords?.map(rk => (
                  <RecommendedCard
                    key={rk.keyword}
                    keyword={rk.keyword}
                    importance={rk.importance}
                    reason={rk.reason}
                    estimatedScoreBoost={rk.estimatedScoreBoost}
                  />
                ))}
              </div>
            </div>
          )}

          {((data.strengths?.length ?? 0) > 0 ||
            (data.weaknesses?.length ?? 0) > 0) && (
            <div>
              <h4 className='mb-3.5 flex items-center gap-2 text-sm font-extrabold text-gray-900'>
                <span className='h-4 w-1 rounded-sm bg-brand' />
                Strengths &amp; Weaknesses
              </h4>
              <div className='grid grid-cols-2 gap-3'>
                {(data.strengths?.length ?? 0) > 0 && (
                  <div className='rounded-xl border border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-[18px]'>
                    <p className='mb-3.5 flex items-center gap-1.5 text-xs font-extrabold text-green-800'>
                      <CheckCircle2 size={14} className='text-green-500' />
                      Strengths
                    </p>
                    <ul className='flex flex-col gap-2.5'>
                      {data.strengths?.map(s => (
                        <li
                          key={s}
                          className='flex items-start gap-2 text-[11px] leading-relaxed text-green-800'
                        >
                          <span className='mt-1 h-[5px] w-[5px] shrink-0 rounded-full bg-green-500' />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(data.weaknesses?.length ?? 0) > 0 && (
                  <div className='rounded-xl border border-red-200 bg-gradient-to-br from-red-50 to-rose-50 p-[18px]'>
                    <p className='mb-3.5 flex items-center gap-1.5 text-xs font-extrabold text-red-900'>
                      <AlertCircle size={14} className='text-red-400' />
                      Weaknesses
                    </p>
                    <ul className='flex flex-col gap-2.5'>
                      {data.weaknesses?.map(w => (
                        <li
                          key={w}
                          className='flex items-start gap-2 text-[11px] leading-relaxed text-red-900'
                        >
                          <span className='mt-1 h-[5px] w-[5px] shrink-0 rounded-full bg-red-400' />
                          {w}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {(data.improvementSuggestions?.length ?? 0) > 0 && (
            <div>
              <h4 className='mb-3.5 flex items-center gap-2 text-sm font-extrabold text-gray-900'>
                <span className='h-4 w-1 rounded-sm bg-brand' />
                Improvement Suggestions
              </h4>
              <div className='space-y-2'>
                {data.improvementSuggestions?.map((tip, i) => (
                  <div
                    key={tip}
                    className='flex items-start gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3.5'
                  >
                    <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-gamma text-[11px] font-extrabold text-brand'>
                      {i + 1}
                    </span>
                    <p className='text-xs leading-relaxed text-gray-600'>
                      {tip}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.overallAnalysis && (
            <div>
              <h4 className='mb-3.5 flex items-center gap-2 text-sm font-extrabold text-gray-900'>
                <span className='h-4 w-1 rounded-sm bg-brand' />
                Overall Analysis
              </h4>
              <div className='rounded-xl border border-brand/10 bg-gradient-to-br from-[#f8faff] to-[#f0f4ff] p-[18px_20px]'>
                <p className='text-xs leading-[1.7] text-gray-600'>
                  {data.overallAnalysis}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='flex items-center justify-end gap-3 border-t border-gray-200 bg-[#fafbfc] px-6 py-4'>
        <Button
          variant='outlined'
          label='Try Another Job'
          handleClickBtn={onTryAnother}
          classes='!ml-0'
        />
        <Button
          variant='contained'
          label='Done'
          handleClickBtn={close}
          classes='!ml-0'
        />
      </div>
    </CommonDialog>
  );
};

export default BoostATSResults;
